import React, { useState } from 'react'
import {
  Carousel as MantineCarousel,
  type Embla,
  type CarouselProps as MantineCarouselProps,
} from '@mantine/carousel'
import styled from '@emotion/styled'
import { spacing } from '../../foundation/spacing'
import { colors } from '../../foundation/colors'
import { type Slide as SlideInterface } from './types'

export interface CarouselProps extends MantineCarouselProps {
  /* 
    An array of the data to show in the carousel
    Data structure looks like: 
    [
      {
        id: 'id1',
        component: <SomeReactComponent />
      },
      {
        id: 'id2',
        component: <SomeReactComponent />
      },
      ...
    ]
  */
  slides: SlideInterface[]
  /* Shows the indicators over the image */
  overlayIndicators?: boolean
}

interface StyledManitineCarouselProps {
  overlayIndicators: boolean
  height?: number | string
}

const StyledMantineCarousel = styled(MantineCarousel, {
  shouldForwardProp: prop => prop !== 'overlayIndicators' && prop !== 'height',
})(({ overlayIndicators, height = 480 }: StyledManitineCarouselProps) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: height,
  padding: spacing.X12,

  '& .mantine-Carousel-indicator': {
    backgroundColor: colors.SECONDARY.REGULAR,
    width: spacing.X8,
    height: spacing.X8,
    borderRadius: '50%',
    transition: 'width 0.3s, height 0.3s',
  },

  '& .mantine-Carousel-control': {
    margin: spacing.X8,
  },

  '& .mantine-Carousel-viewport': {
    width: '100%',
  },

  '& .mantine-Carousel-container': {
    height: '100%',
  },

  ...(!overlayIndicators
    ? {
        '& .mantine-Carousel-indicators': {
          position: 'static',
          marginTop: spacing.X16,
        },
      }
    : {}),
}))

const Carousel = ({
  overlayIndicators = false,
  slides = [],
  ...props
}: CarouselProps): JSX.Element => {
  const [embla, setEmbla] = useState<Embla>()

  return (
    <StyledMantineCarousel
      loop
      withIndicators
      getEmblaApi={setEmbla}
      overlayIndicators={overlayIndicators}
      {...props}
    >
      {slides.map((slide, idx) => (
        <MantineCarousel.Slide key={slide?.id || idx}>
          {typeof slide.component === 'function' ? slide.component(embla) : slide.component}
        </MantineCarousel.Slide>
      ))}
    </StyledMantineCarousel>
  )
}

export default Carousel
